import { useState, useEffect } from "react";
import "../styles/home-surface.css";

function LoadingDots() {
  return (
    <div className="loading-dots-inline">
      {[0, 1, 2].map((i) => (
        <div key={i} className="loading-dot" />
      ))}
    </div>
  );
}

// Progressive reveal while the session is put together — same latency, but
// narrated steps read as "thinking about you" instead of a dead spinner. The
// wording stays on the person, not on the load.
const GEN_STEPS = [
  "Looking at how you've been feeling...",
  "Seeing what you've already asked of yourself this week...",
  "Sizing today to your energy...",
  "Nearly there...",
];

function GenerationProgress() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (step >= GEN_STEPS.length - 1) return;
    const t = setTimeout(() => setStep((s) => s + 1), 1700);
    return () => clearTimeout(t);
  }, [step]);
  return (
    <div className="card" aria-live="polite">
      <LoadingDots />
      <p className="ds-support hs-gen-steps" key={step} style={{ marginTop: "var(--space-4)" }}>
        {GEN_STEPS[step]}
      </p>
    </div>
  );
}

export default function TodayWorkoutCard({
  workout,
  hasTrained,
  aiWorkoutLoading,
  aiWorkoutError,
  onClearAiError,
  onBeginAiWorkout,
  onQuickRegenerate,
  onRegenerateWorkout,
  onStartWorkout,
  onShowGenForm,
}) {
  const [expandedWorkout, setExpandedWorkout] = useState(false);

  if (aiWorkoutLoading) {
    return <GenerationProgress />;
  }

  if (aiWorkoutError) {
    return (
      <section className="card ds-stack ds-fade-in" aria-label="Today's session">
        <p className="ds-body" role="alert">
          {aiWorkoutError}
        </p>
        <button
          className="btn btn-primary btn-full"
          onClick={() => {
            onClearAiError?.();
            onQuickRegenerate?.();
          }}
        >
          Try again
        </button>
        <button
          type="button"
          className="ds-link"
          style={{ display: "block", margin: "0 auto" }}
          onClick={onStartWorkout}
        >
          Or log your own session
        </button>
      </section>
    );
  }

  if (!workout) return null;

  return (
    <section className="card" aria-label="Today's session">
      {/* No eyebrow. The session name is the first thing you read — Headspace
          and Oura let the copy start cold and so does this. */}
      <button
        type="button"
        className="hs-session-toggle"
        aria-expanded={expandedWorkout}
        onClick={() => setExpandedWorkout(!expandedWorkout)}
      >
        <span className="hs-session-text">
          <span className="ds-lead hs-session-name" style={{ display: "block" }}>
            {workout.workout_name}
          </span>
          <span className="ds-support">
            {workout.estimated_duration} min &middot; {workout.focus}
          </span>
        </span>
        {/* The affordance is the chevron, not a line of copy telling you to
            tap. aria-expanded already carries it for screen readers. */}
        <span className="hs-session-chev" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {expandedWorkout && (
        <div className="hs-session-body">
          {workout.warmup && (
            <div className="hs-exercise">
              <p className="hs-key">Warm-up</p>
              <p className="ds-support">{workout.warmup}</p>
            </div>
          )}

          {(workout.exercises || []).map((ex, i) => (
            <div key={i} className="hs-exercise">
              <div className="hs-exercise-head">
                <span className="ds-body">{ex.name}</span>
                <span className="ds-numeral" style={{ fontSize: "var(--text-xs)", color: "var(--text2)" }}>
                  {ex.sets} x {ex.reps}
                </span>
              </div>
              <p className="ds-meta">
                {ex.weight} &middot; rest {ex.rest}
              </p>
              {ex.coaching_note && (
                <p className="ds-support" style={{ marginTop: "var(--space-2)" }}>
                  {ex.coaching_note}
                </p>
              )}
            </div>
          ))}

          {workout.coach_notes && (
            <p className="ds-support" style={{ marginTop: "var(--stack)" }}>
              {workout.coach_notes}
            </p>
          )}
        </div>
      )}

      {hasTrained ? (
        <p className="hs-done-note">That&apos;s done. Nice one.</p>
      ) : (
        <>
          <div className="hs-commit-actions" style={{ marginTop: "var(--stack)" }}>
            <button className="btn btn-primary" onClick={onBeginAiWorkout}>
              Start when you&apos;re ready
            </button>
          </div>
          {/* One row of alternatives. "I moved another way" is the same class of
              action as "Log a run", so it lives with the shortcut chips below
              the card rather than adding a third rung to this ladder. */}
          <div className="hs-quiet-links">
            <button
              type="button"
              className="ds-link"
              onClick={() => onQuickRegenerate?.()}
            >
              Something else
            </button>
            <span className="hs-quiet-sep" aria-hidden="true">&middot;</span>
            <button
              type="button"
              className="ds-link"
              onClick={() => {
                onShowGenForm();
                onRegenerateWorkout();
              }}
            >
              Change the details
            </button>
          </div>
        </>
      )}
    </section>
  );
}
